import { provide } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { takeUntil } from 'rxjs/operators'
import { SchmancyStepsMode, StepsController, stepsContext } from './steps.context'

/** Fires only on a user click of a navigable step's button — never on a `.value=` write, never on first render. */
export type SchmancyStepsNavigateEvent = CustomEvent<{ value: number }>

@customElement('schmancy-steps')
export class SchmancySteps extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	private controller = new StepsController()

	@provide({ context: stepsContext })
	stepsController = this.controller

	@property({ type: Number, reflect: true })
	set value(v: number) {
		this.controller.setValue(v)
	}

	get value(): number {
		return this.controller.value
	}

	/**
	 * The furthest step the reader has reached, when that differs from the one
	 * they are looking at — a wizard they can step back through. Unset (the
	 * default) means "as far as `value`", which is a plain forward-only stepper
	 * and what every consumer that never sets it keeps; a value behind `value`
	 * reads as `value` too, since standing on a step is having reached it.
	 */
	@property({ type: Number, reflect: true })
	set reached(v: number | null) {
		this.controller.setReached(v)
	}

	get reached(): number | null {
		return this.controller.reached
	}

	@property({ type: String, reflect: true })
	set mode(v: SchmancyStepsMode) {
		this.controller.mode = v
	}

	get mode(): SchmancyStepsMode {
		return this.controller.mode
	}

	connectedCallback() {
		super.connectedCallback()
		this.controller.navigateRequest$
			.pipe(takeUntil(this.disconnecting))
			.subscribe(value => this.dispatchScopedEvent('navigate', { value }, { bubbles: false, composed: false }))
	}

	private onSlotChange(e: Event) {
		;(e.target as HTMLSlotElement)
			.assignedElements({ flatten: true })
			.filter((el): el is HTMLElementTagNameMap['schmancy-step'] => el.tagName.toLowerCase() === 'schmancy-step')
			.forEach((step, i) => step.setIndex(i + 1))
	}

	render() {
		return html`
			<nav aria-label="Progress">
				<ol role="list" class="contents">
					<slot @slotchange=${this.onSlotChange}></slot>
				</ol>
			</nav>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-steps': SchmancySteps
	}
}
